import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { enqueueSnackbar } from 'notistack';
import { Coupon, Grant, OrderDetail, OrderPDF, ProductList, StripeCheckoutResponse } from '../../types/products';
import { LazyList, LazyListResponse } from '../../types/types';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, createRequestStatusObject, buildReducers, createLazyList } from '../utils';
import { SingleProduct, UserOrderHistory } from '../../types/products';
import { FreeTierSettings } from './platformSettings';
import i18n from '../../i18n';

export interface ProductState {
	digitalProductsList: LazyList<ProductList>;
	digitalCouponsList: LazyList<Coupon>;
	singleProduct: SingleProduct | null;
	orderHistoryPDF: OrderPDF | null;
	singleOrderHistory: OrderDetail | null;
	userOrderHistory: LazyList<UserOrderHistory>;
	purchasedProductsList: ProductList | null;
	stripeCheckoutUrl: StripeCheckoutResponse | null;

	purchaseProductsRequest: RequestStatusObject;
	digitalProductsListStatus?: RequestStatusObject;
	digitalCouponsListStatus?: RequestStatusObject;
	readSingleProductRequest: RequestStatusObject;
	orderInvoicePDFRequestStatus: RequestStatusObject;
	singleOrderHistoryRequestStatus: RequestStatusObject;
	updateProductOrderRequest: RequestStatusObject;
	userOrderHistoryRequestStatus: RequestStatusObject;
	purchaseProductsRequestStatus: RequestStatusObject;
	StripeCheckoutResponseRequestStatus: RequestStatusObject;
	isLoading: boolean;
}

const initialState: ProductState = {
	digitalProductsList: createLazyList(),
	digitalCouponsList: createLazyList(),
	singleProduct: null as SingleProduct | null,
	orderHistoryPDF: null as OrderPDF | null,
	singleOrderHistory: null as OrderDetail | null,
	userOrderHistory: createLazyList(),
	purchasedProductsList: null as ProductList | null,
	stripeCheckoutUrl: null as StripeCheckoutResponse | null,

	purchaseProductsRequest: createRequestStatusObject(),
	digitalProductsListStatus: createRequestStatusObject(),
	digitalCouponsListStatus: createRequestStatusObject(),
	readSingleProductRequest: createRequestStatusObject(),
	orderInvoicePDFRequestStatus: createRequestStatusObject(),
	singleOrderHistoryRequestStatus: createRequestStatusObject(),
	updateProductOrderRequest: createRequestStatusObject(),
	userOrderHistoryRequestStatus: createRequestStatusObject(),
	StripeCheckoutResponseRequestStatus: createRequestStatusObject(),
	purchaseProductsRequestStatus: createRequestStatusObject(),
	isLoading: false
};

export const getOrderInvoicePDF = createAsyncThunk<
	OrderPDF,
	{ orderId: string },
	{ dispatch: AppDispatch; state: RootState }
>('products/getOrderInvoicePDF', async ({ orderId }) => {
	try {
		const response = await API.get('inspirusapi', `/orders/${orderId}/order-pdf`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});

		return response.data;
	} catch (error: unknown) {
		return 'Failed to get order invoice PDF.';
	}
});

export const purchaseProducts = createAsyncThunk<
	any,
	{
		id: string;
		products: ProductList[];
		priceAdjustment?: boolean;
		payLater?: boolean;
		type?: string;
		currency?: string;
		productNames?: ProductList[];
		userId?: string;
		coupon?: string;
		subscription?: string;
		returnTo?: string;
	},
	{ dispatch: AppDispatch; state: RootState }
>(
	'products/purchaseProducts',
	async ({
		id,
		products,
		priceAdjustment,
		payLater,
		type,
		currency,
		productNames,
		userId,
		coupon,
		subscription,
		returnTo
	}) => {
		const response = await API.post('inspirusapi', `/products/${id}`, {
			headers: {},
			response: true,
			body: { products },
			queryStringParameters: {
				priceAdjustment,
				payLater,
				type: type,
				currency: currency,
				productNames,
				userId,
				coupon,
				subscription,
				returnTo
			}
		});

		return response.data;
	}
);

/**
 * The free tier is not a product record in DynamoDB — it is app-admin configuration
 * applied automatically at sign-up, deliberately kept out of the product table so it can
 * never reach the Stripe checkout. It is spliced into the purchasable list here so it
 * still appears alongside the paid plans.
 */
const buildFreeTierProduct = (freeTier: FreeTierSettings) => ({
	id: 'free-tier',
	name: freeTier.name,
	description: freeTier.description,
	category: ['user'],
	price: 0,
	yearlyPrice: 0,
	monthlyPrice: 0,
	creationDateTime: '2025-04-15T10:22:08.282Z',
	type: 'license',
	productColor: '#417505',
	subscription: 'No Expiry',
	// Zero allowances are omitted rather than advertised as "0" on the plan card.
	grants: freeTier.grants.filter((grant) => typeof grant.value !== 'number' || grant.value > 0) as Grant[],
	sortNumber: { user: { N: '0' } }
});

export const getDigitalProducts = createAsyncThunk<
	ProductList[],
	{
		type?: string;
		category?: string[];
		visible?: boolean;
		priceAdjustment?: boolean;
		currency?: string;
		organisationId?: string;
		subscriptionExpire?: string;
		annualCost?: boolean;
	},
	{ dispatch: AppDispatch; state: RootState }
>(
	'products/getDigitalProducts',
	async (
		{
			type,
			category,
			visible,
			priceAdjustment = false,
			organisationId,
			subscriptionExpire,
			annualCost = false,
			currency = 'usd'
		},
		{ rejectWithValue, getState }
	) => {
		const freeTierProduct = buildFreeTierProduct(getState().platformSettings.freeTier) as ProductList;
		let route;
		if (organisationId) {
			route = `/products/organisations/${organisationId}`;
		} else {
			route = '/products';
		}

		const maxAttempts = 3;
		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			try {
				const response = await API.get('inspirusapi', route, {
					headers: {},
					response: true,
					queryStringParameters: {
						type,
						category: category?.join(','),
						visible,
						priceAdjustment,
						subscriptionExpire,
						annualCost,
						currency
					}
				});

				const data = response.data;

				// A populated array is a definitive success.
				if (Array.isArray(data) && data.length > 0) {
					return [freeTierProduct, ...data];
				}

				// An empty array (or unexpected shape) is treated as a transient
				// response and retried. On the final attempt, accept an empty array
				// (so the free tier still renders) but reject anything else.
				if (attempt === maxAttempts) {
					return Array.isArray(data)
						? [freeTierProduct, ...data]
						: rejectWithValue('Failed to fetch digital products.');
				}
			} catch (error: unknown) {
				if (attempt === maxAttempts) {
					return rejectWithValue('Failed to fetch digital products.');
				}
			}

			// Back off briefly before retrying.
			await new Promise((resolve) => setTimeout(resolve, attempt * 400));
		}

		return rejectWithValue('Failed to fetch digital products.');
	}
);

export const getDigitalCoupons = createAsyncThunk<any, undefined, { dispatch: AppDispatch; state: RootState }>(
	'products/getDigitalCoupons',
	async (_, { rejectWithValue }) => {
		try {
			const response = await API.get('inspirusapi', '/products/coupons', {
				headers: {},
				response: true,
				queryStringParameters: {}
			});

			return response.data;
		} catch (error: unknown) {
			return rejectWithValue('Failed to fetch digital coupons.');
		}
	}
);

export const fetchSingleProduct = createAsyncThunk<
	SingleProduct,
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('products/getSingleProduct', async ({ id }) => {
	const response = await API.get('inspirusapi', `/products/${id}`, {
		headers: {},
		response: true
	});

	return response.data;
});

export const getSingleOrder = createAsyncThunk<
	OrderDetail,
	{ orderId: string },
	{ dispatch: AppDispatch; state: RootState }
>('products/getSingleOrder', async ({ orderId }) => {
	const response = await API.get('inspirusapi', `/orders/${orderId}`, {
		headers: {},
		response: true
	});

	return response.data;
});

export const updateProductOrder = createAsyncThunk<
	any,
	{ productId: string; sortNumber: number; organisationType: string },
	{ dispatch: AppDispatch; state: RootState }
>('products/updateProductOrder', async ({ productId, sortNumber, organisationType }) => {
	const response = await API.put('inspirusapi', `/products/${productId}/set-sort-order`, {
		headers: {},
		response: true,
		body: {},
		queryStringParameters: {
			sortNumber,
			organisationType
		}
	});

	return response.data;
});

export const fetchUserOrderHistory = createAsyncThunk<
	LazyListResponse<UserOrderHistory>,
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('products/fetchUserOrderHistory', async ({ userId }) => {
	const response = await API.get('inspirusapi', `/orders/user/${userId}`, {
		headers: {},
		response: true
	});

	return response.data;
});

export const getPurchasedProducts = createAsyncThunk<
	ProductList[],
	{
		id: string;
	},
	{ dispatch: AppDispatch; state: RootState }
>('products/purchasedProducts', async ({ id }) => {
	const response = await API.get('inspirusapi', `/products/purchased/${id}`, {
		headers: {},
		response: true
	});

	return response.data;
});

const productSlice = createSlice({
	name: 'products',
	initialState,
	reducers: {
		resetProducts: (state) => {
			state = initialState;
			return state;
		},
		clearSingleProduct: (state) => {
			state.singleProduct = null;
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, purchaseProducts, 'purchaseProductsRequest', {
			onFulfilled: (state, payload) => {
				state.stripeCheckoutUrl = payload;
			}
		});
		buildReducers(builder, getDigitalProducts, 'digitalProductsListStatus', {
			onFulfilled: (state, payload) => {
				state.digitalProductsList.items = Array.isArray(payload) ? payload : [];
			}
		});
		buildReducers(builder, getDigitalCoupons, 'digitalCouponsListStatus', {
			onFulfilled: (state, payload) => {
				state.isLoading = false;
				state.digitalCouponsList.items = payload;
				enqueueSnackbar(i18n.t('pages.admin.digitalCoupons.getAllCouponSuccess'), {
					variant: 'success'
				});
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.grants.getAllCouponFailure'), { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, fetchSingleProduct, 'readSingleProductRequest', {
			onFulfilled: (state, payload) => {
				state.singleProduct = payload;
			}
		});
		buildReducers(builder, getOrderInvoicePDF, 'orderInvoicePDFRequestStatus', {
			onFulfilled: (state, payload) => {
				state.orderHistoryPDF = payload;
			}
		});
		buildReducers(builder, getSingleOrder, 'singleOrderHistoryRequestStatus', {
			onFulfilled: (state, payload) => {
				state.singleOrderHistory = payload;
			}
		});
		buildReducers(builder, updateProductOrder, 'updateProductOrderRequest', {
			onFulfilled: () => {
				enqueueSnackbar('Product order updated successfully', { variant: 'success' });
			}
		});
		buildReducers(builder, fetchUserOrderHistory, 'userOrderHistoryRequestStatus', {
			onFulfilled: (state, payload) => {
				state.userOrderHistory = payload;
			}
		});
		buildReducers(builder, getPurchasedProducts, 'purchaseProductsRequestStatus', {
			onFulfilled: (state, payload) => {
				state.purchasedProductsList = payload;
			}
		});
	}
});

export const { resetProducts, clearSingleProduct } = productSlice.actions;
export default productSlice.reducer;
